// ── Easing + interpolation utilities ──

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smooth Hermite interpolation. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Smoother (Ken Perlin's improved version). Used for premium-feel transitions. */
export function smootherstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Cubic ease out — the workhorse for premium UI motion. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Quint ease in-out — smoother than cubic for hero moments. */
export function easeInOutQuint(t: number): number {
  return t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2;
}

/** Spring damping — used to settle position/scale with overshoot. */
export class SpringValue {
  private value: number;
  private velocity = 0;

  constructor(
    initial: number,
    private stiffness: number = 180,
    private damping: number = 22,
  ) {
    this.value = initial;
  }

  get current(): number {
    return this.value;
  }

  setTarget(target: number, dt: number): number {
    const dx = target - this.value;
    const accel = this.stiffness * dx - this.damping * this.velocity;
    this.velocity += accel * dt;
    this.value += this.velocity * dt;
    return this.value;
  }

  reset(value: number): void {
    this.value = value;
    this.velocity = 0;
  }
}
